import { Alert, Button, Group, Paper, Stack, Text, Textarea, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconPhoneIncoming } from '@tabler/icons-react';
import { useRef, useState } from 'react';
import { uploadAttachment } from '../api/attachments';
import { ApiError, ESCALATION_CLOSED_ERROR_CODE } from '../api/http';
import { postEscalationComment, type EscalationComment } from '../api/result';
import type { InboundCallSectionConfig } from '../types/schema';
import AttachmentUploadField from './AttachmentUploadField';

interface InboundCallFormProps {
  config: InboundCallSectionConfig;
  resultId: string;
  primaryColor: string;
  sessionEmail?: string;
  submissionBlocked: boolean;
  onCommentSubmitted: (comment: EscalationComment) => void;
  onSubmissionSucceeded: () => Promise<void>;
}

function createIdempotencyKey(): string {
  return typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

const CLOSED_ESCALATION_MESSAGE = 'Additional comments and documents may not be added to a closed escalation. Please submit a new escalation to proceed.';

export default function InboundCallForm({ config, resultId, primaryColor, sessionEmail, submissionBlocked, onCommentSubmitted, onSubmissionSucceeded }: InboundCallFormProps) {
  const [reporterName, setReporterName] = useState('');
  const [note, setNote] = useState('');
  const [filesByField, setFilesByField] = useState<Record<string, File[]>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [blockedByRejection, setBlockedByRejection] = useState(false);
  const idempotencyKeysRef = useRef(new WeakMap<File, string>());
  const maxLength = config.noteField?.maxCharLength ?? 5000; // default to 5000 if not specified

  const getIdempotencyKey = (file: File) => {
    const existing = idempotencyKeysRef.current.get(file);
    if (existing) return existing;
    const key = createIdempotencyKey();
    idempotencyKeysRef.current.set(file, key);
    return key;
  };

  const selectedFiles = (config.uploadFields ?? []).flatMap((field) =>
    (filesByField[field.id] ?? []).map((file) => ({ fieldId: field.id, file })),
  );
  const trimmedNote = note.trim();
  const trimmedReporterName = reporterName.trim();
  const reporterNameRequired = config.reporterNameField?.required ?? false;
  const isBlocked = submissionBlocked || blockedByRejection;
  const canSubmit = !isBlocked && (!reporterNameRequired || trimmedReporterName) && Boolean(trimmedNote || selectedFiles.length > 0);
  const maxAttachmentsPerSubmission = (config.uploadFields ?? [])
    .reduce((total, field) => total + (field.maxFiles ?? 3), 0);

  const handleFilesChange = (fieldId: string, files: File[]) => {
    const otherFieldCount = Object.entries(filesByField)
      .filter(([currentFieldId]) => currentFieldId !== fieldId)
      .reduce((total, [, currentFiles]) => total + currentFiles.length, 0);
    if (otherFieldCount + files.length > maxAttachmentsPerSubmission) {
      notifications.show({
        title: 'Attachment Limit',
        message: `Select no more than ${maxAttachmentsPerSubmission} attachments for this escalation.`,
        color: 'red',
      });
      return;
    }
    setFilesByField((current) => ({ ...current, [fieldId]: files }));
  };

  const handleSubmit = async () => {
    if (!canSubmit || isBlocked) return;
    setIsSubmitting(true);

    const operations: Array<{ kind: 'note' } | { kind: 'file'; fieldId: string; file: File }> = [];
    const requests: Promise<void>[] = [];
    
    if (trimmedNote) {
      operations.push({ kind: 'note' });
      requests.push(postEscalationComment('escalations', resultId, trimmedNote, trimmedReporterName || undefined));
    }
    
    for (const selected of selectedFiles) {
      operations.push({ kind: 'file', ...selected });
      requests.push(uploadAttachment('escalations', resultId, selected.fieldId, selected.file, getIdempotencyKey(selected.file)));
    }

    const results = await Promise.allSettled(requests);
    let succeeded = 0;
    const successfulFiles = new Set<File>();
    results.forEach((result, index) => {
      if (result.status !== 'fulfilled') {
        const operation = operations[index];
        console.error('Inbound call submission failed', {
          kind: operation.kind,
          ...(operation.kind === 'file' ? { fieldId: operation.fieldId, fileName: operation.file.name } : {}),
          error: result.reason,
        });
        return;
      }
      succeeded += 1;
      const operation = operations[index];
      if (operation.kind === 'note') {
        setNote('');
        setReporterName('');
        onCommentSubmitted({
          id: `comment-${Date.now()}`,
          timestamp: new Date().toISOString(),
          text: trimmedNote,
          author: trimmedReporterName || sessionEmail,
          authorEmail: sessionEmail,
        });
      } else {
        successfulFiles.add(operation.file);
      }
    });
    if (successfulFiles.size > 0) {
      setFilesByField((current) => Object.fromEntries(
        Object.entries(current).map(([fieldId, files]) => [fieldId, files.filter((file) => !successfulFiles.has(file))]),
      ));
    }

    const failed = results.length - succeeded;
    const escalationClosed = results.some((result) =>
      result.status === 'rejected' &&
      result.reason instanceof ApiError &&
      result.reason.errorCode === ESCALATION_CLOSED_ERROR_CODE,
    );
    if (escalationClosed) {
      setBlockedByRejection(true);
      notifications.show({
        title: 'Escalation Closed',
        message: CLOSED_ESCALATION_MESSAGE,
        color: 'red',
      });
    }
    const attachmentConflict = results.some((result, index) =>
      result.status === 'rejected' &&
      operations[index].kind === 'file' &&
      result.reason instanceof ApiError &&
      result.reason.status === 409,
    );
    const notificationTitle = attachmentConflict && succeeded === 0
      ? 'Attachment Could Not Be Added'
      : failed === 0
        ? 'Call Logged'
        : succeeded > 0
          ? 'Partially Submitted'
          : 'Submission Failed';
    const notificationMessage = attachmentConflict
      ? succeeded > 0
        ? 'Some information was submitted, but one or more attachments could not be added. This escalation may have reached its attachment limit.'
        : 'This escalation may have reached its attachment limit. Remove the file or contact support if the problem continues.'
      : failed === 0
        ? 'The inbound call information was submitted.'
        : succeeded > 0
          ? `${succeeded} item${succeeded === 1 ? '' : 's'} submitted; ${failed} failed and can be retried.`
          : 'The inbound call information could not be submitted. Please try again.';
    if (!escalationClosed) {
      notifications.show({
        title: notificationTitle,
        message: notificationMessage,
        color: failed === 0 ? 'green' : succeeded > 0 ? 'yellow' : 'red',
      });
    }

    if (succeeded > 0) {
      try {
        await onSubmissionSucceeded();
      } catch {
        notifications.show({
          title: 'Activity Not Refreshed',
          message: 'The call was submitted, but the activity log could not be refreshed.',
          color: 'yellow',
        });
      }
    }
    setIsSubmitting(false);
  };

  return (
    <Paper p="md" radius="md" withBorder className="portal-panel">
      <Stack gap="md">
        <Group gap="xs" align="center">
          <IconPhoneIncoming size={16} color={primaryColor} />
          <Text className="portal-kicker">{config.title}</Text>
        </Group>
        {config.description && <Text size="sm" c="dimmed">{config.description}</Text>}
        {isBlocked && (
          <Alert color="blue" variant="light" title="This escalation is closed">
            <Text size="sm">{CLOSED_ESCALATION_MESSAGE}</Text>
          </Alert>
        )}
        {config.reporterNameField && (
          <div>
            <Text size="sm" fw={500} mb="xs">
              {config.reporterNameField.label ?? ''}
              {config.reporterNameField.required && <Text span c="red"> *</Text>}
            </Text>
            <TextInput
              value={reporterName}
              onChange={(event) => setReporterName(event.target.value)}
              placeholder={config.reporterNameField.placeholder}
              disabled={isSubmitting || isBlocked}
              required={config.reporterNameField.required}
            />
          </div>
        )}
        {config.noteField && (
          <div>
            <Text size="sm" fw={500} mb="xs">{config.noteField.label ?? ''}</Text>
            <Textarea
              value={note}
              onChange={(event) => setNote(event.target.value.slice(0,maxLength))}
              placeholder={config.noteField.placeholder}
              resize="vertical"
              disabled={isSubmitting || isBlocked}
              autosize
              minRows={3}
              maxRows={50}
              styles={{ input: { minHeight: 'calc(3 * 1.55rem + 1rem)' } }}
            />
            <Text size="xs" c="dimmed" ta="right" mt={4}>{note.length} / {maxLength} characters</Text>
          </div>
        )}
        {(config.uploadFields ?? []).map((field) => (
          <AttachmentUploadField
            key={field.id}
            field={field}
            value={filesByField[field.id] ?? []}
            onChange={(files) => handleFilesChange(field.id, files)}
            disabled={isSubmitting || isBlocked}
            removeDisabled={isSubmitting}
          />
        ))}
        <Group justify="flex-end">
          <Button
            size="sm"
            disabled={!canSubmit}
            loading={isSubmitting}
            onClick={() => { void handleSubmit(); }}
            color={primaryColor}
          >
            {config.submitLabel}
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
}